import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Download, FileText, Loader2, Printer, Wallet } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August",
  "September", "October", "November", "December"];

const download = async (url, filename) => {
  const { data } = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
};

export default function Payouts() {
  const { company, user } = useAuth();
  const isOwner = user?.role === "owner";
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [view, setView] = useState("monthly");
  const [data, setData] = useState(null);
  const [statement, setStatement] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState({ amount: "", method: "bank_transfer", reference: "", paid_on: "" });
  const [includeAll, setIncludeAll] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setError("");
    setData(null);
    try {
      const url = view === "annual"
        ? `/reports/payouts/annual?year=${year}&include_all=${includeAll}`
        : `/reports/payouts?year=${year}&month=${month}`;
      setData((await api.get(url)).data);    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  }, [view, year, month, includeAll]);

  useEffect(() => { load(); }, [load]);

  const openStatement = async (email) => {
    setError("");
    try {
      const { data: s } = await api.get(
        `/reports/payouts/statement?email=${encodeURIComponent(email)}&year=${year}` +
        (view === "annual" ? "" : `&month=${month}`));
      setStatement(s);
      setPay({ amount: String(s.totals.total || ""), method: "bank_transfer", reference: "",
        paid_on: new Date().toISOString().slice(0, 10) });
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const settle = async (fn) => {
    setBusy(true);
    setError("");
    try {
      const { data: res } = await fn();
      setStatement((s) => ({ ...s, settlement: res.settlement }));
      await load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const approve = (email) => settle(() => api.post("/reports/payouts/approve", {
    email, year, month: view === "annual" ? null : month,
    amount: Number(pay.amount || 0), method: pay.method, reference: pay.reference, paid_on: pay.paid_on,
  }));

  const unapprove = (email) => settle(() => api.delete(
    `/reports/payouts/approve?email=${encodeURIComponent(email)}&year=${year}` +
    (view === "annual" ? "" : `&month=${month}`)));

  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);

  if (statement) {
    return (
      <div data-testid="payout-statement" className="max-w-3xl space-y-6">
        <div className="no-print flex flex-wrap items-center gap-3">
          <Button data-testid="statement-back" variant="outline" onClick={() => setStatement(null)}
            className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">← All chauffeurs</Button>
          <Button data-testid="statement-print" onClick={() => window.print()}
            className="bg-gold text-ink-900 hover:bg-gold-light"><Printer className="mr-2 h-4 w-4" /> Print / save PDF</Button>
          <Button data-testid="statement-csv" variant="outline"
            onClick={() => download(
              `/reports/payouts/export.csv?year=${year}${view === "annual" ? "" : `&month=${month}`}&email=${encodeURIComponent(statement.chauffeur.email)}`,
              `payout-${statement.chauffeur.email}.csv`)}
            className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
            <Download className="mr-2 h-4 w-4" /> CSV
          </Button>
        </div>

        <div className="rounded-xl border border-ink-600 bg-ink-800 p-8 print:bg-white print:text-black">
          <div className="flex items-start justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">Chauffeur earnings statement</p>
              <h1 className="mt-3 text-2xl font-bold text-slate-50 print:text-black">{statement.chauffeur.name || statement.chauffeur.email}</h1>
              <p className="mt-1 text-sm text-slate-400">{statement.chauffeur.email}</p>
              <p className="mt-3 font-mono text-xs text-slate-500">{statement.period} · {statement.company.name}</p>
            </div>
            <div className="text-right">
              {statement.company.logo_url && (
                <img src={statement.company.logo_url} alt="" className="h-12 w-auto object-contain" />
              )}
              {statement.settlement ? (
                <div data-testid="settlement-stamp" className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-2">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-emerald-300">Paid</p>
                  <p className="mt-1 text-sm text-slate-200 print:text-black">
                    {money(statement.settlement.amount)} · {statement.settlement.method_label}
                  </p>
                  <p className="font-mono text-[10px] text-slate-500">
                    {statement.settlement.paid_on}{statement.settlement.reference ? ` · ${statement.settlement.reference}` : ""}
                  </p>
                </div>
              ) : (
                <p data-testid="settlement-pending" className="mt-3 font-mono text-[11px] uppercase tracking-widest text-amber-300">
                  Payment pending
                </p>
              )}
            </div>
          </div>

          {isOwner && (
            <div className="no-print mt-6 rounded-lg border border-ink-600 bg-ink-900/60 p-4">
              {statement.settlement ? (
                <button data-testid="settlement-undo" onClick={() => unapprove(statement.chauffeur.email)}
                  className="font-mono text-[11px] uppercase tracking-widest text-red-400">
                  Undo this payment
                </button>
              ) : (
                <div className="space-y-3">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Mark this statement as paid</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                    <Input data-testid="settlement-amount" type="number" step="0.01" value={pay.amount}
                      onChange={(e) => setPay({ ...pay, amount: e.target.value })} placeholder="Amount"
                      className="h-10 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
                    <Input data-testid="settlement-date" type="date" value={pay.paid_on}
                      onChange={(e) => setPay({ ...pay, paid_on: e.target.value })}
                      className="h-10 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
                    <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
                      <SelectTrigger data-testid="settlement-method" className="h-10 border-ink-600 bg-ink-800 text-slate-100">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-ink-600 bg-ink-800 text-slate-100">
                        {[["bank_transfer", "Bank transfer"], ["cash", "Cash"], ["cheque", "Cheque"], ["other", "Other"]]
                          .map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input data-testid="settlement-reference" value={pay.reference}
                      onChange={(e) => setPay({ ...pay, reference: e.target.value })} placeholder="Reference (optional)"
                      className="h-10 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
                  </div>
                  <Button data-testid="settlement-save" disabled={busy}
                    onClick={() => approve(statement.chauffeur.email)}
                    className="bg-gold text-ink-900 hover:bg-gold-light">
                    <Check className="mr-2 h-4 w-4" /> Mark as paid
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[["Trips", statement.totals.trips], ["Base payout", money(statement.totals.base)],
              ["Tips", money(statement.totals.tips)], ["Total paid", money(statement.totals.total)]].map(([l, v]) => (
              <div key={l} className="rounded-lg border border-ink-600 bg-ink-900/50 p-4 print:border-slate-300 print:bg-white">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{l}</p>
                <p className="mt-1.5 font-display text-xl text-slate-50 print:text-black">{v}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead>
              <tr className="border-b border-ink-600 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                <th className="py-2">Trip</th><th className="py-2">Date</th><th className="py-2">Passenger</th>
                <th className="py-2 text-right">Miles</th><th className="py-2 text-right">Base</th>
                <th className="py-2 text-right">Tip</th><th className="py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody data-testid="statement-lines">
              {statement.trips.map((t) => (
                <tr key={t.manifest_id} className="border-b border-ink-700/60 text-slate-300 print:text-black">
                  <td className="py-2.5 font-mono text-xs text-gold">#{t.number}</td>
                  <td className="py-2.5">{t.trip_date}</td>
                  <td className="py-2.5">{t.customer_name}</td>
                  <td className="py-2.5 text-right">{t.miles.toFixed(1)}</td>
                  <td className="py-2.5 text-right">{money(t.base)}</td>
                  <td className="py-2.5 text-right">{money(t.tip)}</td>
                  <td className="py-2.5 text-right text-slate-100 print:text-black">{money(t.total)}</td>
                </tr>
              ))}
              {statement.trips.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-slate-500">No completed trips in this period.</td></tr>
              )}
            </tbody>
          </table>
          </div>
          <p className="mt-6 text-xs text-slate-500">
            Independent contractor earnings summary. Retain for your records — this is not a tax filing.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="payouts-page" className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Finance</p>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-50">Chauffeur payouts</h1>
          <p className="mt-2 text-sm text-slate-400">
            Every completed trip, what each chauffeur earned and the year-end 1099 picture for {company?.name}.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {view === "annual" && (
            <>
              <Button data-testid="payouts-annual-pdf" variant="outline"
                onClick={() => download(`/reports/payouts/annual.pdf?year=${year}&include_all=${includeAll}`,
                  `1099-summary-${year}.pdf`)}
                className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
                <FileText className="mr-2 h-4 w-4" /> Summary PDF
              </Button>
            </>
          )}
          <Button data-testid="payouts-csv" variant="outline"
            onClick={() => download(
              view === "annual" ? `/reports/payouts/annual.csv?year=${year}&include_all=${includeAll}`
                : `/reports/payouts/export.csv?year=${year}&month=${month}`,
              view === "annual" ? `1099-summary-${year}.csv` : `payouts-${year}-${month}.csv`)}
            className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      {error && <p data-testid="payouts-error" className="text-sm text-red-400">{error}</p>}

      <div className="flex flex-wrap items-center gap-2">
        {[["monthly", "Monthly statement"], ["annual", "Year-end 1099 summary"]].map(([k, l]) => (
          <button key={k} data-testid={`payouts-view-${k}`} onClick={() => setView(k)}
            className={`rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors ${
              view === k ? "border-gold/40 bg-gold/10 text-gold" : "border-ink-600 text-slate-400 hover:text-slate-100"}`}>
            {l}
          </button>
        ))}
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger data-testid="payouts-year" className="h-10 w-[110px] border-ink-600 bg-ink-800 text-slate-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-ink-600 bg-ink-800 text-slate-100">
            {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        {view === "monthly" && (
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger data-testid="payouts-month" className="h-10 w-[150px] border-ink-600 bg-ink-800 text-slate-100">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-ink-600 bg-ink-800 text-slate-100">
              {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {busy && <Loader2 className="h-4 w-4 animate-spin text-gold" />}
      </div>

      {view === "annual" && (
        <div className="flex flex-wrap items-center gap-4 rounded-xl border border-ink-600 bg-ink-800/60 p-4">
          <button data-testid="payouts-threshold-toggle" onClick={() => setIncludeAll(!includeAll)}
            className={`rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors ${
              includeAll ? "border-ink-600 text-slate-400 hover:text-slate-100"
                : "border-gold/40 bg-gold/10 text-gold"}`}>
            {includeAll ? "Showing every chauffeur" : "Only $600+ (1099 reportable)"}
          </button>
          <p className="text-xs text-slate-400">
            {includeAll
              ? `Including ${data?.below_threshold ?? 0} chauffeur(s) paid under $600 — usually no 1099 required.`
              : `${data?.below_threshold ?? 0} chauffeur(s) paid under $600 are hidden.`}
            {(data?.missing_tax_details ?? 0) > 0 && (
              <span data-testid="payouts-missing-tax" className="ml-2 text-amber-300">
                {data.missing_tax_details} missing TIN / address — add them on{" "}
                <Link to="/app/chauffeurs" className="underline">Chauffeurs</Link>.
              </span>
            )}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[["Chauffeurs paid", data?.totals?.chauffeurs ?? 0], ["Trips", data?.totals?.trips ?? 0],
          ["Tips", money(data?.totals?.tips)], ["Total payout", money(data?.totals?.total)]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-ink-600 bg-ink-800 p-5">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{l}</p>
            <p className="mt-2 font-display text-2xl text-slate-50">{v}</p>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-ink-600 bg-ink-800">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead>
            <tr className="border-b border-ink-600 font-mono text-[10px] uppercase tracking-widest text-slate-500">
              <th className="p-4">Chauffeur</th><th className="p-4 text-right">Trips</th>
              <th className="p-4 text-right">Miles</th><th className="p-4 text-right">Base payout</th>
              <th className="p-4 text-right">Tips</th><th className="p-4 text-right">Total</th>
              <th className="p-4">Settled</th>
              {view === "annual" && <th className="p-4">Filing</th>}
              <th className="p-4" />
            </tr>
          </thead>
          <tbody data-testid="payouts-rows">
            {(data?.rows || []).map((r) => (
              <tr key={r.chauffeur_email} className="border-b border-ink-700/60">
                <td className="p-4">
                  <p className="text-slate-100">{r.chauffeur_name}</p>
                  <p className="font-mono text-[11px] text-slate-500">{r.chauffeur_email}</p>
                </td>
                <td className="p-4 text-right text-slate-300">{r.trips}</td>
                <td className="p-4 text-right text-slate-300">{r.miles.toFixed(1)}</td>
                <td className="p-4 text-right text-slate-300">{money(r.base)}</td>
                <td className="p-4 text-right text-emerald-300">{money(r.tips)}</td>
                <td className="p-4 text-right font-display text-lg text-slate-50">{money(r.total)}</td>
                <td className="p-4">
                  {r.settlement ? (
                    <span data-testid={`settled-${r.chauffeur_email}`} className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
                      Paid {r.settlement.paid_on}
                    </span>
                  ) : (
                    <span data-testid={`unsettled-${r.chauffeur_email}`} className="font-mono text-[10px] uppercase tracking-widest text-amber-300">
                      Pending
                    </span>
                  )}
                </td>
                {view === "annual" && (
                  <td className="p-4 font-mono text-[11px] text-slate-400">
                    {r.form}
                    {!r.tax_ready && (
                      <span className="ml-1 text-amber-300" title="TIN or address missing">·  needs TIN</span>
                    )}
                  </td>
                )}
                <td className="p-4 text-right">
                  <div className="flex items-center justify-end gap-4">
                    {view === "annual" && (
                      <button data-testid={`payout-1099-${r.chauffeur_email}`}
                        onClick={() => download(
                          `/reports/payouts/1099.pdf?year=${year}&email=${encodeURIComponent(r.chauffeur_email)}`,
                          `1099-NEC-${year}-${r.chauffeur_email}.pdf`)}
                        className="font-mono text-[11px] uppercase tracking-widest text-slate-400 hover:text-gold">
                        1099 PDF
                      </button>
                    )}
                    <button data-testid={`payout-statement-${r.chauffeur_email}`}
                      onClick={() => openStatement(r.chauffeur_email)}
                      className="font-mono text-[11px] uppercase tracking-widest text-gold">Statement →</button>
                  </div>
                </td>
              </tr>
            ))}
            {(data?.rows || []).length === 0 && !busy && (
              <tr><td colSpan={9} className="p-10 text-center">
                <Wallet className="mx-auto h-6 w-6 text-slate-600" />
                <p data-testid="payouts-empty" className="mt-3 text-sm text-slate-500">
                  No completed trips in {data?.period || "this period"} yet.
                </p>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {view === "annual" && (data?.rows || []).length > 0 && (
        <p className="text-xs text-slate-500">
          Chauffeurs paid $600 or more in a calendar year generally require a 1099-NEC. Confirm with your accountant.
        </p>
      )}
    </div>
  );
}
